import React, { useEffect, useRef, useState, useCallback } from 'react';

// Advanced Lip-Sync Avatar with Phoneme Detection and Natural Movements
export default function AnimatedAvatar({ isPlaying, isMuted, audioRef }) {
  // Viseme states (mouth shapes for different sounds)
  const [viseme, setViseme] = useState('rest'); // rest, A, E, I, O, U, M, F, L, W
  const [mouthOpenness, setMouthOpenness] = useState(0);
  const [eyeState, setEyeState] = useState('open'); // open, half, closed
  const [eyebrowState, setEyebrowState] = useState('neutral'); // neutral, raised, slight
  const [headRotation, setHeadRotation] = useState({ x: 0, y: 0, z: 0 });
  const [shoulderOffset, setShoulderOffset] = useState(0);
  const [expressionIntensity, setExpressionIntensity] = useState(0);
  
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceCreatedRef = useRef(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Avatar image
  const avatarImage = "https://customer-assets.emergentagent.com/job_learnhub-lms-7/artifacts/6lkg0t0z_Avatoon-Avatar-133-300x300.webp";

  // Map audio characteristics to visemes
  const getVisemeFromAudio = useCallback((frequencyData, volume) => {
    if (volume < 0.1) return 'rest';
    
    // Analyze frequency bands to estimate phonemes
    const lowFreq = frequencyData.slice(0, 4).reduce((a, b) => a + b, 0) / 4;
    const midFreq = frequencyData.slice(4, 12).reduce((a, b) => a + b, 0) / 8;
    const highFreq = frequencyData.slice(12, 24).reduce((a, b) => a + b, 0) / 12;
    
    const total = lowFreq + midFreq + highFreq;
    if (total < 30) return 'rest';
    
    // Map frequency patterns to visemes
    const lowRatio = lowFreq / (total + 1);
    const highRatio = highFreq / (total + 1);
    
    if (highRatio > 0.5) {
      // High frequency = S, F, TH sounds
      return Math.random() > 0.5 ? 'F' : 'E';
    } else if (lowRatio > 0.5) {
      // Low frequency = M, B, P, O, U sounds
      return Math.random() > 0.5 ? 'O' : 'M';
    } else if (midFreq > highFreq && midFreq > lowFreq) {
      // Mid frequency = A, E sounds
      return Math.random() > 0.5 ? 'A' : 'E';
    } else {
      // Mixed = I, L sounds
      return Math.random() > 0.5 ? 'I' : 'L';
    }
  }, []);

  // Audio analysis and animation loop
  useEffect(() => {
    if (!isPlaying || !audioRef?.current) {
      setViseme('rest');
      setMouthOpenness(0);
      return;
    }

    let animating = true;

    const setupAudioAnalysis = async () => {
      try {
        // Create audio context if needed
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        const audioContext = audioContextRef.current;
        
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Create analyser if needed
        if (!analyserRef.current) {
          analyserRef.current = audioContext.createAnalyser();
          analyserRef.current.fftSize = 128;
          analyserRef.current.smoothingTimeConstant = 0.3;
        }

        // Connect audio source (only once)
        if (!sourceCreatedRef.current && audioRef.current) {
          try {
            const source = audioContext.createMediaElementSource(audioRef.current);
            source.connect(analyserRef.current);
            analyserRef.current.connect(audioContext.destination);
            sourceCreatedRef.current = true;
          } catch (e) {
            // Source might already be connected
            console.log('Audio source already connected');
          }
        }

        const frequencyData = new Uint8Array(analyserRef.current.frequencyBinCount);
        const timeData = new Uint8Array(analyserRef.current.fftSize);

        const animate = () => {
          if (!animating || !isPlaying) {
            setViseme('rest');
            setMouthOpenness(0);
            return;
          }

          // Get audio data
          analyserRef.current.getByteFrequencyData(frequencyData);
          analyserRef.current.getByteTimeDomainData(timeData);

          // Calculate volume
          const volume = frequencyData.reduce((a, b) => a + b, 0) / frequencyData.length / 255;
          
          // Get viseme based on audio analysis
          const newViseme = getVisemeFromAudio(frequencyData, volume);
          setViseme(newViseme);
          setMouthOpenness(Math.min(volume * 2.5, 1));

          // Expression intensity based on volume
          setExpressionIntensity(volume);

          // Natural head movement - subtle bobbing with speech
          const time = Date.now() / 1000;
          setHeadRotation({
            x: Math.sin(time * 0.8) * 1.5 + volume * 3,
            y: Math.sin(time * 0.5) * 2,
            z: Math.sin(time * 0.3) * 0.8
          });

          // Shoulder movement
          setShoulderOffset(Math.sin(time * 0.4) * 1 + volume * 2);

          // Eyebrow movement based on emphasis
          if (volume > 0.4) {
            setEyebrowState('raised');
          } else if (volume > 0.2) {
            setEyebrowState('slight');
          } else {
            setEyebrowState('neutral');
          }

          animationRef.current = requestAnimationFrame(animate);
        };

        animate();
      } catch (e) {
        console.log('Audio analysis setup failed, using fallback:', e);
        // Fallback animation without audio analysis
        fallbackAnimation();
      }
    };

    const fallbackAnimation = () => {
      const visemes = ['rest', 'A', 'E', 'O', 'M', 'I', 'A', 'rest', 'E', 'O'];
      let index = 0;
      
      const animate = () => {
        if (!animating || !isPlaying) {
          setViseme('rest');
          setMouthOpenness(0);
          return;
        }

        index = (index + 1) % visemes.length;
        setViseme(visemes[index]);
        setMouthOpenness(visemes[index] === 'rest' ? 0 : 0.3 + Math.random() * 0.5);
        
        const time = Date.now() / 1000;
        setHeadRotation({
          x: Math.sin(time * 0.8) * 2,
          y: Math.sin(time * 0.5) * 2,
          z: Math.sin(time * 0.3) * 1
        });

        setTimeout(() => {
          animationRef.current = requestAnimationFrame(animate);
        }, 80);
      };
      
      animate();
    };

    setupAudioAnalysis();

    return () => {
      animating = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, audioRef, getVisemeFromAudio]);

  // Natural eye blinking
  useEffect(() => {
    let blinkTimeout;
    
    const blink = () => {
      setEyeState('closed');
      setTimeout(() => {
        setEyeState('half');
        setTimeout(() => {
          setEyeState('open');
        }, 50);
      }, 80);
    };

    const scheduleBlink = () => {
      const delay = 2000 + Math.random() * 3000;
      blinkTimeout = setTimeout(() => {
        blink();
        // Occasional double blink
        if (Math.random() < 0.2) {
          setTimeout(blink, 300);
        }
        scheduleBlink();
      }, delay);
    };

    scheduleBlink();
    return () => clearTimeout(blinkTimeout);
  }, []);

  // Get mouth shape SVG based on viseme
  const getMouthShape = () => {
    const baseX = 150;
    const baseY = 188;
    const openness = mouthOpenness;
    
    // Mouth shapes for different visemes (phoneme groups)
    const shapes = {
      rest: { // Closed, slight smile
        path: `M ${baseX - 25} ${baseY} Q ${baseX} ${baseY + 5} ${baseX + 25} ${baseY}`,
        isOpen: false
      },
      A: { // "ah" - wide open
        width: 30 + openness * 15,
        height: 12 + openness * 20,
        teethHeight: 8,
        showTongue: true
      },
      E: { // "ee" - wide, less tall
        width: 28 + openness * 12,
        height: 8 + openness * 12,
        teethHeight: 6,
        showTongue: false
      },
      I: { // "ih" - medium
        width: 22 + openness * 10,
        height: 10 + openness * 14,
        teethHeight: 6,
        showTongue: false
      },
      O: { // "oh" - rounded
        width: 18 + openness * 8,
        height: 16 + openness * 18,
        teethHeight: 5,
        showTongue: true,
        rounded: true
      },
      U: { // "oo" - small rounded
        width: 14 + openness * 5,
        height: 14 + openness * 12,
        teethHeight: 4,
        showTongue: false,
        rounded: true
      },
      M: { // "mm" - closed
        path: `M ${baseX - 22} ${baseY + 2} Q ${baseX} ${baseY + 8} ${baseX + 22} ${baseY + 2}`,
        isOpen: false,
        pressed: true
      },
      F: { // "ff" - teeth on lip
        width: 26 + openness * 8,
        height: 6 + openness * 8,
        teethHeight: 10,
        showTongue: false,
        teethVisible: true
      },
      L: { // "ll" - tongue tip
        width: 24 + openness * 10,
        height: 10 + openness * 14,
        teethHeight: 6,
        showTongue: true,
        tongueUp: true
      },
      W: { // "ww" - pursed
        width: 12 + openness * 4,
        height: 12 + openness * 10,
        teethHeight: 3,
        showTongue: false,
        rounded: true,
        pursed: true
      }
    };

    const shape = shapes[viseme] || shapes.rest;

    if (!shape.isOpen && shape.path) {
      // Closed mouth shapes
      return (
        <g>
          <path 
            d={shape.path}
            stroke={shape.pressed ? "#A06060" : "#C08080"}
            strokeWidth={shape.pressed ? "5" : "4"}
            fill="none"
            strokeLinecap="round"
          />
        </g>
      );
    }

    // Open mouth shapes
    const { width, height, teethHeight, showTongue, rounded, tongueUp, pursed } = shape;
    
    return (
      <g>
        {/* Mouth opening */}
        {rounded || pursed ? (
          <ellipse 
            cx={baseX} 
            cy={baseY + height/4} 
            rx={width/2} 
            ry={height/2}
            fill="#2D1010"
            stroke="#A06060"
            strokeWidth="2"
          />
        ) : (
          <path
            d={`M ${baseX - width/2} ${baseY - height/4} 
                Q ${baseX} ${baseY - height/3} ${baseX + width/2} ${baseY - height/4}
                Q ${baseX + width/2 + 4} ${baseY + height/3} ${baseX} ${baseY + height/2}
                Q ${baseX - width/2 - 4} ${baseY + height/3} ${baseX - width/2} ${baseY - height/4}`}
            fill="#2D1010"
            stroke="#A06060"
            strokeWidth="2"
          />
        )}
        
        {/* Upper teeth */}
        {teethHeight > 3 && (
          <rect 
            x={baseX - width/2 + 4} 
            y={baseY - height/4} 
            width={width - 8} 
            height={Math.min(teethHeight, height/2)}
            rx="2"
            fill="#FAFAFA"
          />
        )}
        
        {/* Tongue */}
        {showTongue && height > 10 && (
          <ellipse 
            cx={baseX} 
            cy={tongueUp ? baseY + height/6 : baseY + height/3}
            rx={width/3}
            ry={tongueUp ? height/5 : height/4}
            fill="#C06060"
          />
        )}
        
        {/* Lip shine */}
        <ellipse 
          cx={baseX} 
          cy={baseY + height/3}
          rx={width/4}
          ry="2"
          fill="white"
          opacity="0.15"
        />
      </g>
    );
  };

  // Get eye shape based on state
  const getEyeShape = (isLeft) => {
    const cx = isLeft ? 108 : 192;
    const cy = 128;
    
    const heights = {
      open: 16,
      half: 8,
      closed: 2
    };
    
    const eyeHeight = heights[eyeState];
    
    return (
      <ellipse 
        cx={cx} 
        cy={cy} 
        rx="18" 
        ry={eyeHeight}
        fill="#F5DEB3"
      />
    );
  };

  return (
    <div className="avatar-container relative w-full h-full flex flex-col items-center justify-center">
      <div className="relative">
        {/* Ambient glow when speaking */}
        {isPlaying && (
          <div 
            className="absolute -inset-8 rounded-3xl blur-2xl transition-opacity duration-300"
            style={{
              background: `radial-gradient(circle, rgba(0,86,210,${0.15 + expressionIntensity * 0.2}) 0%, transparent 70%)`,
            }}
          />
        )}
        
        {/* Avatar container with natural head movement */}
        <div 
          className="relative overflow-visible"
          style={{
            width: '300px',
            height: '300px',
            transform: `
              perspective(1000px)
              rotateX(${headRotation.x}deg)
              rotateY(${headRotation.y}deg)
              rotateZ(${headRotation.z}deg)
              translateY(${shoulderOffset}px)
            `,
            transition: 'transform 0.1s ease-out',
            transformStyle: 'preserve-3d'
          }}
        >
          {/* Clean background */}
          <div 
            className="absolute inset-0 rounded-2xl"
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              boxShadow: isPlaying 
                ? '0 0 50px rgba(0, 86, 210, 0.3), 0 20px 60px rgba(0,0,0,0.4)' 
                : '0 15px 50px rgba(0,0,0,0.3)'
            }}
          />
          
          {/* Avatar image */}
          <img 
            src={avatarImage}
            alt="Instructor"
            className="absolute inset-0 w-full h-full object-cover rounded-2xl"
            onLoad={() => setImageLoaded(true)}
            style={{
              filter: `brightness(${1 + expressionIntensity * 0.05})`,
            }}
          />
          
          {/* Animated face overlay */}
          {imageLoaded && (
            <svg 
              viewBox="0 0 300 300" 
              className="absolute inset-0 w-full h-full"
              style={{ overflow: 'visible' }}
            >
              {/* Eye blinks */}
              {eyeState !== 'open' && (
                <>
                  {getEyeShape(true)}
                  {getEyeShape(false)}
                </>
              )}
              
              {/* Eyebrow expressions */}
              {eyebrowState !== 'neutral' && (
                <g style={{ 
                  transform: `translateY(${eyebrowState === 'raised' ? -4 : -2}px)`,
                  transition: 'transform 0.15s ease-out'
                }}>
                  {/* Eyebrow highlights - subtle raise effect */}
                  <path d="M 85 108 Q 108 100 130 108" stroke="#F5DEB3" strokeWidth="8" fill="none" opacity="0.6" />
                  <path d="M 170 108 Q 192 100 215 108" stroke="#F5DEB3" strokeWidth="8" fill="none" opacity="0.6" />
                </g>
              )}
              
              {/* Lip-synced mouth */}
              {getMouthShape()}
            </svg>
          )}
          
          {/* LIVE indicator */}
          {isPlaying && (
            <div className="absolute top-4 right-4 z-10">
              <div className="flex items-center gap-1.5 bg-red-500 rounded-full px-3 py-1.5 shadow-lg">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white text-xs font-bold tracking-wide">LIVE</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Name badge */}
        <div className="mt-4 text-center">
          <div className="inline-block bg-gradient-to-r from-slate-800/90 to-slate-700/90 backdrop-blur-sm rounded-xl px-6 py-3 shadow-xl border border-slate-600/30">
            <p className="text-white font-semibold text-lg">Prof. James Wilson</p>
            <p className="text-[#0056D2] text-sm font-medium">Senior Instructor</p>
          </div>
        </div>
      </div>
      
      {/* Audio visualization */}
      {isPlaying && (
        <div className="mt-6 flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm rounded-full px-6 py-3 border border-slate-700/50">
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div
                key={i}
                className="w-1 bg-gradient-to-t from-[#0056D2] to-[#00A3FF] rounded-full transition-all duration-75"
                style={{
                  height: `${6 + mouthOpenness * 20 + Math.sin(Date.now() / 100 + i) * 4}px`,
                }}
              />
            ))}
          </div>
          <span className="text-white/90 text-sm font-medium ml-2">Speaking</span>
        </div>
      )}
      
      {/* Idle prompt */}
      {!isPlaying && imageLoaded && (
        <div className="mt-6 text-slate-400 text-sm flex items-center gap-2 bg-slate-800/40 rounded-full px-4 py-2">
          <div className="w-8 h-8 rounded-full bg-[#0056D2]/20 flex items-center justify-center">
            <svg className="w-4 h-4 text-[#0056D2]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
          </div>
          <span>Click "Start Lesson" to begin</span>
        </div>
      )}
    </div>
  );
}
